/* 0.49.01 - A number read from text the way people write it, for sum(),
 * max() and min(): "12,000", "12 000", "12.000,50", "1,500.75 AMD",
 * "֏ 3500". Thousands separators (comma, dot, space, no-break spaces,
 * apostrophe) are dropped, the decimal mark is worked out, and a
 * currency word or sign at either end is ignored. Anything else, or a
 * text with letters in the middle, gives null.
 *
 * Which mark is the decimal one:
 *   both , and .        the one that comes last
 *   one kind, repeated   a thousands separator (1.234.567, 1,234,567)
 *   one , or . once      a decimal mark, unless exactly 3 digits follow
 *                        and the platform groups with it (12,000 is
 *                        twelve thousand, 12,5 is twelve and a half)
 */
import { getNumberFormatDefaults } from '../utils/numberFormat';

const SPACES = /[\s\u00A0\u202F\u2009']/g;

/* The platform's thousands separator, from its number locale (the one
   format() writes with). A space or anything else gives ''. */
export function platformGroupMark() {
  try {
    const loc = (getNumberFormatDefaults() || {}).defaultLocale || undefined;
    const part = new Intl.NumberFormat(loc).formatToParts(12345).find(p => p.type === 'group');
    return part && (part.value === ',' || part.value === '.') ? part.value : '';
  } catch (_e) {
    return ',';
  }
}

export function looseNumber(value, groupMark = platformGroupMark()) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  let s = value.trim();
  if (s === '') return null;
  // 1.500 is fifteen hundred where the platform groups with a dot.
  if (groupMark === '.' && /^-?\d{1,3}(\.\d{3})+$/.test(s)) return Number(s.replace(/\./g, ''));
  const plain = Number(s);
  if (Number.isFinite(plain)) return plain;
  // A currency word or sign at the start or the end.
  s = s.replace(/^[^\d+\-.,]+/, '').replace(/[^\d.,]+$/, '').trim();
  let sign = '';
  if (s.startsWith('-') || s.startsWith('+')) { sign = s[0] === '-' ? '-' : ''; s = s.slice(1); }
  s = s.replace(SPACES, '');
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  let dec = null;
  if (lastComma >= 0 && lastDot >= 0) dec = lastComma > lastDot ? ',' : '.';
  else {
    const mark = lastComma >= 0 ? ',' : (lastDot >= 0 ? '.' : null);
    if (mark) {
      const count = s.split(mark).length - 1;
      const after = s.length - s.lastIndexOf(mark) - 1;
      if (count > 1) dec = null;
      else if (after === 3 && mark === groupMark) dec = null;
      else dec = mark;
    }
  }
  const group = dec === ',' ? '.' : (dec === '.' ? ',' : null);
  if (group) s = s.split(group).join('');
  else if (!dec) s = s.replace(/[.,]/g, '');
  if (dec) {
    if (s.split(dec).length > 2) return null;
    s = s.replace(dec, '.');
  }
  if (!/^\d*\.?\d+$|^\d+\.?$/.test(s)) return null;
  const n = Number(sign + s);
  return Number.isFinite(n) ? n : null;
}
